import { enqueueSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { Environment } from '~/engine/data/Environment';
import { getErrorMessage } from '~/utils/errors';
import { waitForUI } from '~/utils/uiUtils';
import { useGetOrCreateDestination } from '../shared/useGetOrCreateDestination';

interface UseRingApplicationOptions {
    environment: Environment | null;
    inputRoi: RegionOfInterest | undefined;
    onSuccess: () => void;
    updateView: () => void;
}

export function useRingApplication({
    environment,
    inputRoi,
    onSuccess,
    updateView,
}: UseRingApplicationOptions) {
    const [isProcessing, setIsProcessing] = useState(false);
    const { getOrCreateDestination } = useGetOrCreateDestination({
        environment,
        inputRoi,
    });

    const handleApplyRing = useCallback(
        async (
            lowerMm: number,
            upperMm: number,
            outputRoiNumber: number | null,
            newName?: string
        ) => {
            if (isProcessing) {
                enqueueSnackbar(
                    'Another ring operation is already in progress.',
                    { variant: 'warning' }
                );
                return;
            }

            if (!environment?.structureSet || !inputRoi?.texture) {
                return;
            }

            setIsProcessing(true);

            try {
                const dest = await getOrCreateDestination(
                    outputRoiNumber,
                    newName
                );
                if (!dest) {
                    setIsProcessing(false);
                    return;
                }

                enqueueSnackbar('Applying ring...', { variant: 'info' });
                // TODO: improve kind of hacky rendering solutions should be removed and
                // heavy computation should be lifted away from blocking the main thread for smooth rendering.
                await waitForUI();
                try {
                    const ringTexture = await SDFOperations.applyRing(
                        inputRoi.texture,
                        lowerMm,
                        upperMm
                    );
                    dest.replaceTexture(ringTexture, 'Ring');
                    updateView();
                    enqueueSnackbar(`Applied ring to "${dest.name}"`, {
                        variant: 'success',
                    });
                    onSuccess();
                } catch (e) {
                    console.error('Ring apply error:', e);
                    enqueueSnackbar(
                        `Failed to apply ring: ${getErrorMessage(e)}`,
                        { variant: 'error' }
                    );
                } finally {
                    setIsProcessing(false);
                }
            } catch (e) {
                setIsProcessing(false);
                console.error('Ring operation error:', e);
                enqueueSnackbar(`Failed to apply ring: ${getErrorMessage(e)}`, {
                    variant: 'error',
                });
            }
        },
        [
            environment,
            inputRoi,
            isProcessing,
            getOrCreateDestination,
            updateView,
            onSuccess,
        ]
    );

    return {
        isProcessing,
        handleApplyRing,
    };
}
